import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import LeftPanel from '../components/planner/LeftPanel';
import OverviewView from '../components/planner/OverviewView';
import TimelineView from '../components/planner/TimelineView';
import FullScreenMapModal from '../components/planner/FullScreenMapModal';
import MapTabView from '../components/planner/MapTabView';
import LocationAutocomplete from '../components/planner/LocationAutocomplete';

import Button from '../components/ui/Button';
import Toast from '../components/ui/Toast';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from 'react-resizable-panels';
import { Save, Share2, Download, ArrowLeft, X, PanelLeftClose, PanelLeftOpen, Link2, MessageCircle, Check, Sparkles, LayoutList, Map as MapIcon } from 'lucide-react';
import SendToConcierge from '../components/concierge/SendToConcierge';
import { publishItinerary } from '../api/itineraries';
import { generateHotels } from '../api/atlas';
import { ACTIVITY_CATEGORIES } from '../constants/travelerProfiles';
import {
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import {
  isRecentlyCreated,
  getDateForDay,
  getDayLocationLabels,
} from '../components/planner/plannerHelpers';

// Fix Leaflet default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Notes Modal Component
function NotesModal({ activity, onClose, onSave }) {
  const [notes, setNotes] = useState(activity?.custom_notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(activity.id, notes);
    setSaving(false);
    onClose();
  };

  if (!activity) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-platinum-200">
          <h3 className="text-lg font-semibold text-charcoal-500">
            Notes for {activity.title}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-platinum-200 rounded">
            <X size={20} className="text-platinum-600" />
          </button>
        </div>
        <div className="p-4">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add your personal notes here... (e.g., reservation codes, tips, reminders)"
            className="w-full h-32 px-3 py-2 border border-platinum-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-400 resize-none"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-platinum-200">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Notes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function detectCategoryFromTitle(title) {
  const t = title.toLowerCase();
  if (/lunch|dinner|breakfast|brunch|restaurant|café|cafe|food|eat|cocktail|wine|beer|tapas|sushi|pizza|grill|street food/.test(t)) return 'food';
  if (/hike|trek|trail|nature|park|beach|waterfall|mountain|forest|lake|swim|wildlife|garden|safari/.test(t)) return 'nature';
  if (/museum|gallery|art|history|historic|castle|temple|church|cathedral|monument|palace|tour|culture|heritage|ruins/.test(t)) return 'culture';
  if (/adventure|zip|bungee|surf|dive|kayak|climb|rock|raft|skydive|paraglide|cycle/.test(t)) return 'adventure';
  if (/spa|massage|yoga|relax|retreat|thermal|wellness/.test(t)) return 'relaxation';
  if (/shop|market|mall|boutique|souvenir|shopping/.test(t)) return 'shopping';
  if (/club|nightclub|nightlife|party|concert|show|dance/.test(t)) return 'nightlife';
  if (/hotel|hostel|camp|check.?in|check.?out/.test(t)) return 'accommodation';
  if (/bus|train|flight|ferry|transfer|taxi|uber|transit/.test(t)) return 'transport';
  return null;
}

const TIME_OF_DAY_OPTIONS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
  { value: 'all-day', label: 'All Day' },
];

function CategoryChips({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {ACTIVITY_CATEGORIES.map(cat => {
        const isActive = value === cat.value;
        return (
          <button
            key={cat.value}
            type="button"
            onClick={() => onChange(cat.value)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all border ${
              isActive ? 'text-white border-transparent shadow-sm' : 'bg-white text-charcoal-400 border-platinum-200 hover:border-platinum-300'
            }`}
            style={isActive ? { backgroundColor: cat.color, borderColor: cat.color } : {}}
          >
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.7)' : cat.color }} />
            {cat.label.split(' ')[0]}
          </button>
        );
      })}
    </div>
  );
}

function TimeOfDayPills({ value, onChange }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {TIME_OF_DAY_OPTIONS.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
            value === opt.value
              ? 'bg-coral-400 text-white border-coral-400'
              : 'bg-white text-charcoal-400 border-platinum-200 hover:border-platinum-300'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// Add Activity Modal Component
function AddActivityModal({ dayLabel, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    latitude: null,
    longitude: null,
    category: 'other',
    duration_hours: '',
    duration_minutes: 0,
    time_of_day: 'morning',
    estimated_cost_min: '',
    estimated_cost_max: ''
  });
  const [categoryLocked, setCategoryLocked] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (categoryLocked) return;
    const detected = detectCategoryFromTitle(formData.title);
    if (detected) setFormData(prev => ({ ...prev, category: detected }));
    if (!formData.title) setCategoryLocked(false);
  }, [formData.title, categoryLocked]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategorySelect = (value) => {
    setCategoryLocked(true);
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    setSaving(true);
    const durationSet = formData.duration_hours !== '' && parseInt(formData.duration_hours) > 0;
    const duration = durationSet
      ? parseInt(formData.duration_hours) * 60 + parseInt(formData.duration_minutes || 0)
      : null;
    await onSave({
      title: formData.title.trim(),
      description: formData.description,
      location: formData.location,
      latitude: formData.latitude,
      longitude: formData.longitude,
      category: formData.category,
      duration_minutes: duration,
      time_of_day: formData.time_of_day,
      estimated_cost_min: formData.estimated_cost_min ? parseFloat(formData.estimated_cost_min) : null,
      estimated_cost_max: formData.estimated_cost_max ? parseFloat(formData.estimated_cost_max) : null
    });
    setSaving(false);
    onClose();
  };

  const inputCls = "w-full px-3 py-2 border border-platinum-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-400 text-sm";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-platinum-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="text-base font-semibold text-charcoal-500">Add Activity to {dayLabel}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-platinum-100 rounded-lg transition-colors">
            <X size={18} className="text-platinum-500" />
          </button>
        </div>

        <div className="px-5 pt-5 pb-4 space-y-4">
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && formData.title.trim()) handleSave(); }}
            placeholder="What are you doing? e.g. Visit the Eiffel Tower"
            className="w-full px-4 py-3 border-2 border-platinum-200 rounded-xl focus:outline-none focus:border-coral-400 text-sm font-medium placeholder:text-platinum-400 transition-colors"
            autoFocus
          />

          <div>
            <label className="block text-xs font-medium text-charcoal-400 mb-2">Category</label>
            <CategoryChips value={formData.category} onChange={handleCategorySelect} />
          </div>

          <LocationAutocomplete
            value={formData.location}
            onChange={({ location, latitude, longitude }) =>
              setFormData(prev => ({ ...prev, location, latitude, longitude }))
            }
          />

          <div>
            <label className="block text-xs font-medium text-charcoal-400 mb-2">When?</label>
            <TimeOfDayPills value={formData.time_of_day} onChange={(v) => handleChange('time_of_day', v)} />
          </div>

          <button
            onClick={() => setShowMore(v => !v)}
            className="flex items-center gap-1.5 text-xs text-platinum-500 hover:text-coral-400 transition-colors"
          >
            <span className="text-base leading-none">{showMore ? '▾' : '▸'}</span>
            {showMore ? 'Hide details' : 'Duration, notes & cost'}
          </button>

          {showMore && (
            <div className="space-y-3 pt-1">
              <div>
                <label className="block text-xs text-platinum-500 mb-1">Duration <span className="text-platinum-400">(optional)</span></label>
                <div className="flex gap-2">
                  <select value={formData.duration_hours} onChange={(e) => handleChange('duration_hours', e.target.value)} className={inputCls}>
                    <option value="">— not set</option>
                    {[1,2,3,4,5,6,7,8].map(h => <option key={h} value={h}>{h}h</option>)}
                  </select>
                  {formData.duration_hours !== '' && (
                    <select value={formData.duration_minutes} onChange={(e) => handleChange('duration_minutes', e.target.value)} className={inputCls}>
                      {[0,15,30,45].map(m => <option key={m} value={m}>{m}min</option>)}
                    </select>
                  )}
                </div>
              </div>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Notes (optional)"
                className={`${inputCls} h-16 resize-none`}
              />
              <div>
                <label className="block text-xs text-platinum-500 mb-1">Estimated cost <span className="text-platinum-400">(optional)</span></label>
                <div className="flex gap-2 items-center">
                  <input type="number" value={formData.estimated_cost_min} onChange={(e) => handleChange('estimated_cost_min', e.target.value)}
                    placeholder="Min $" className={inputCls} />
                  <span className="text-platinum-400 text-sm flex-shrink-0">to</span>
                  <input type="number" value={formData.estimated_cost_max} onChange={(e) => handleChange('estimated_cost_max', e.target.value)}
                    placeholder="Max $" className={inputCls} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-platinum-100 sticky bottom-0 bg-white rounded-b-2xl">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !formData.title.trim()}>
            {saving ? 'Adding…' : 'Add Activity'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Edit Activity Modal — pre-filled, all fields visible
function EditActivityModal({ activity, onClose, onSave }) {
  const existingHours = activity?.duration_minutes ? Math.floor(activity.duration_minutes / 60) : '';
  const existingMins = activity?.duration_minutes ? activity.duration_minutes % 60 : 0;

  const [formData, setFormData] = useState({
    title: activity?.title || '',
    description: activity?.description || '',
    location: activity?.location || '',
    latitude: activity?.latitude ?? null,
    longitude: activity?.longitude ?? null,
    category: activity?.category || 'other',
    duration_hours: existingHours,
    duration_minutes: existingMins,
    time_of_day: activity?.time_of_day || 'morning',
    estimated_cost_min: activity?.estimated_cost_min ?? '',
    estimated_cost_max: activity?.estimated_cost_max ?? '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    setSaving(true);
    const durationSet = formData.duration_hours !== '' && parseInt(formData.duration_hours) > 0;
    const duration = durationSet
      ? parseInt(formData.duration_hours) * 60 + parseInt(formData.duration_minutes || 0)
      : null;
    await onSave(activity.id, {
      title: formData.title.trim(),
      description: formData.description,
      location: formData.location,
      latitude: formData.latitude,
      longitude: formData.longitude,
      category: formData.category,
      duration_minutes: duration,
      time_of_day: formData.time_of_day,
      estimated_cost_min: formData.estimated_cost_min !== '' ? parseFloat(formData.estimated_cost_min) : null,
      estimated_cost_max: formData.estimated_cost_max !== '' ? parseFloat(formData.estimated_cost_max) : null,
    });
    setSaving(false);
    onClose();
  };

  if (!activity) return null;

  const inputCls = "w-full px-3 py-2 border border-platinum-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-400 text-sm";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-platinum-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="text-base font-semibold text-charcoal-500">Edit Activity</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-platinum-100 rounded-lg transition-colors">
            <X size={18} className="text-platinum-500" />
          </button>
        </div>

        <div className="px-5 pt-5 pb-4 space-y-4">
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="w-full px-4 py-3 border-2 border-platinum-200 rounded-xl focus:outline-none focus:border-coral-400 text-sm font-medium transition-colors"
            autoFocus
          />

          <div>
            <label className="block text-xs font-medium text-charcoal-400 mb-2">Category</label>
            <CategoryChips value={formData.category} onChange={(v) => handleChange('category', v)} />
          </div>

          <LocationAutocomplete
            value={formData.location}
            onChange={({ location, latitude, longitude }) =>
              setFormData(prev => ({ ...prev, location, latitude, longitude }))
            }
          />

          <div>
            <label className="block text-xs font-medium text-charcoal-400 mb-2">When?</label>
            <TimeOfDayPills value={formData.time_of_day} onChange={(v) => handleChange('time_of_day', v)} />
          </div>

          <div>
            <label className="block text-xs text-platinum-500 mb-1">Duration <span className="text-platinum-400">(optional)</span></label>
            <div className="flex gap-2">
              <select value={formData.duration_hours} onChange={(e) => handleChange('duration_hours', e.target.value)} className={inputCls}>
                <option value="">— not set</option>
                {[1,2,3,4,5,6,7,8].map(h => <option key={h} value={h}>{h}h</option>)}
              </select>
              {formData.duration_hours !== '' && (
                <select value={formData.duration_minutes} onChange={(e) => handleChange('duration_minutes', e.target.value)} className={inputCls}>
                  {[0,15,30,45].map(m => <option key={m} value={m}>{m}min</option>)}
                </select>
              )}
            </div>
          </div>

          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Notes or description (optional)"
            className={`${inputCls} h-16 resize-none`}
          />

          {/* Estimated Cost */}
          <div>
            <label className="block text-xs text-platinum-500 mb-1">Estimated cost <span className="text-platinum-400">(optional)</span></label>
            <div className="flex gap-2 items-center">
              <input type="number" value={formData.estimated_cost_min} onChange={(e) => handleChange('estimated_cost_min', e.target.value)}
                placeholder="Min $" className={inputCls} />
              <span className="text-platinum-400 text-sm flex-shrink-0">to</span>
              <input type="number" value={formData.estimated_cost_max} onChange={(e) => handleChange('estimated_cost_max', e.target.value)}
                placeholder="Max $" className={inputCls} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-platinum-100 sticky bottom-0 bg-white rounded-b-2xl">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !formData.title.trim()}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const TIME_ORDER = { morning: 0, afternoon: 1, evening: 2, night: 3, 'all-day': 4 };
function getTimeRank(timeOfDay) {
  return TIME_ORDER[timeOfDay] ?? 5;
}

/** Sort activities by time_of_day within the same day, preserving relative order for same-time activities. */
function sortByTimeOfDay(dayActivities) {
  return [...dayActivities].sort((a, b) => getTimeRank(a.time_of_day) - getTimeRank(b.time_of_day));
}

/**
 * After a drag, adopt the time_of_day of the neighbour the activity landed against
 * (the row above, or the row below when dropped at the top). Without this the row
 * moves but keeps its old label, so the visible order and the time contradict.
 */
function resolveDroppedTimeOfDay(ordered, index) {
  return ordered[index - 1]?.time_of_day
    || ordered[index + 1]?.time_of_day
    || ordered[index]?.time_of_day
    || null;
}

function PlannerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: authLoading } = useAuth();
  const [itinerary, setItinerary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingActivities, setGeneratingActivities] = useState(false);
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'timeline'
  const [activeId, setActiveId] = useState(null);
  const [selectedDay, setSelectedDay] = useState('all');
  const [notesActivity, setNotesActivity] = useState(null); // For notes modal
  const [editActivity, setEditActivity] = useState(null); // For edit activity modal
  const [dragOverDay, setDragOverDay] = useState(null); // For visual drop feedback
  const [addActivityDay, setAddActivityDay] = useState(null); // For add activity modal
  const [showAssistant, setShowAssistant] = useState(true);
  const [showMap, setShowMap] = useState(true);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [generationTimedOut, setGenerationTimedOut] = useState(false);
  const [accommodations, setAccommodations] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [generatingHotels, setGeneratingHotels] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [dayNotes, setDayNotes] = useState({}); // { [dayNumber]: noteText }
  const [dayTitles, setDayTitles] = useState({}); // { [dayNumber]: titleText }
  const [highlightedDays, setHighlightedDays] = useState(new Set());
  const [feedbackBanner, setFeedbackBanner] = useState(null); // 'regenerating' | 'done' | null
  const [toast, setToast] = useState(null); // { type: 'success'|'error'|'info', message: string }
  const [showFullScreenMap, setShowFullScreenMap] = useState(false);
  const [atlasPlanDismissed, setAtlasPlanDismissed] = useState(false);
  const [mobilePanelView, setMobilePanelView] = useState('days');
  const [mapMounted, setMapMounted] = useState(false);
  const [aiMounted, setAiMounted] = useState(false);
  const dismissToast = useCallback(() => setToast(null), []);
  const previousActivitiesRef = useRef([]);

  // Augment accommodations from DB with day numbers computed from dates
  const augmentAccommodations = (accs, startDate) => {
    if (!accs) return [];
    return accs.map(acc => {
      if (acc.check_in_day && acc.check_out_day) return acc;
      if (!startDate || !acc.check_in_date || !acc.check_out_date) return acc;
      const start = new Date(startDate);
      const checkIn = new Date(acc.check_in_date);
      const checkOut = new Date(acc.check_out_date);
      const checkInDay = Math.round((checkIn - start) / (1000 * 60 * 60 * 24)) + 1;
      const checkOutDay = Math.round((checkOut - start) / (1000 * 60 * 60 * 24)) + 1;
      return { ...acc, check_in_day: checkInDay, check_out_day: checkOutDay };
    });
  };

  const isGenerating = location.state?.generating;
  const isManualTrip = location.state?.manual; // blank template — never poll for AI activities

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Shared polling logic — used by initial generation AND re-check
  const startPolling = (timeoutMs = 180000, expectedDays = null) => {
    setGeneratingActivities(true);
    setGenerationTimedOut(false);
    let msgIndex = 0;
    let lastCount = 0;
    let stablePolls = 0; // Track consecutive polls with same activity count

    const progressMessages = [
      'Building your itinerary...',
      'Adding activities...',
      'Almost done...',
    ];

    setProgressMessage(progressMessages[0]);
    const messageInterval = setInterval(() => {
      msgIndex++;
      const idx = Math.min(msgIndex, progressMessages.length - 1);
      setProgressMessage(progressMessages[idx]);
    }, 5000);

    const finishPolling = async (activitiesData) => {
      setGeneratingActivities(false);
      setGenerationTimedOut(false);
      setProgressMessage('');

      // Compute highlighted days if we had previous activities (regeneration)
      const prev = previousActivitiesRef.current;
      if (prev.length > 0) {
        const changed = new Set();
        activitiesData.forEach(a => {
          const old = prev.find(o => o.day_number === a.day_number && o.position === a.position);
          if (!old || old.title !== a.title) changed.add(a.day_number);
        });
        setHighlightedDays(changed);
        setIsRegenerating(false);
        setFeedbackBanner('done');
        previousActivitiesRef.current = [];
        setTimeout(() => {
          setHighlightedDays(new Set());
          setFeedbackBanner(null);
        }, 5000);
      }

      const [updatedItineraryResult, accResult] = await Promise.all([
        supabase.from('itineraries').select('*').eq('id', id).single(),
        supabase.from('accommodations').select('*').eq('itinerary_id', id)
      ]);
      if (updatedItineraryResult.data) setItinerary(updatedItineraryResult.data);
      if (accResult.data) setAccommodations(augmentAccommodations(accResult.data, updatedItineraryResult.data?.start_date));
    };

    const pollInterval = setInterval(async () => {
      const { data: activitiesData } = await supabase
        .from('activities')
        .select('*')
        .eq('itinerary_id', id)
        .order('day_number', { ascending: true })
        .order('position', { ascending: true });

      if (activitiesData && activitiesData.length > 0) {
        // Show activities immediately as they arrive
        setActivities(activitiesData);

        // Update progress message once we have some data
        if (msgIndex < 3) {
          setProgressMessage('Adding activities...');
        }

        // Check if generation is complete (stable count for 3 consecutive polls)
        if (activitiesData.length === lastCount) {
          stablePolls++;
        } else {
          stablePolls = 0;
          lastCount = activitiesData.length;
        }

        // Require all days to be present before stopping — prevents stopping early
        // when a fast-finishing chunk lands first (multi-city parallel generation)
        if (stablePolls >= 3) {
          const daysCovered = new Set(activitiesData.map(a => a.day_number)).size;
          const allDaysCovered = !expectedDays || daysCovered >= expectedDays;
          if (allDaysCovered) {
            clearInterval(pollInterval);
            clearInterval(messageInterval);
            await finishPolling(activitiesData);
          } else {
            // Not all days in yet — reset stable counter and keep waiting
            stablePolls = 0;
          }
        }
      }
    }, 3000);

    const timeout = setTimeout(() => {
      clearInterval(pollInterval);
      clearInterval(messageInterval);
      // If we have some activities, treat as success
      if (lastCount > 0) {
        setGeneratingActivities(false);
        setProgressMessage('');
        // Don't show timeout UI — we got partial results
      } else {
        setGeneratingActivities(false);
        setProgressMessage('');
        setGenerationTimedOut(true);
      }
      setIsRegenerating(false);
      setFeedbackBanner(null);
      previousActivitiesRef.current = [];
    }, timeoutMs);

    return () => {
      clearInterval(pollInterval);
      clearInterval(messageInterval);
      clearTimeout(timeout);
    };
  };

  // Retry: re-trigger the backend API call AND restart polling
  const handleRetryGeneration = async () => {
    if (!itinerary) return;
    setGenerationTimedOut(false);

    // Re-fire the backend API call with retry on cold-start
    const apiUrl = process.env.REACT_APP_API_URL || 'https://travelatlas.onrender.com';
    const retryBody = JSON.stringify({
      itineraryId: id,
      destination: itinerary.destination,
      tripLength: itinerary.trip_length,
      travelPace: itinerary.travel_pace,
      budget: itinerary.budget,
      travelerProfiles: itinerary.traveler_profiles || [],
      region: null,
      tripOrigin: itinerary.trip_origin || null,
      travelMode: itinerary.travel_mode || null,
      mustDoActivities: itinerary.must_do_activities || null,
      whoIsComing: itinerary.who_is_coming || null,
    });
    const fireRetry = async (attempt = 0) => {
      try {
        const res = await fetch(`${apiUrl}/api/ai/generate-itinerary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: retryBody,
        });
        if (!res.ok && attempt < 3) {
          // Wake server with no-cors ping then retry
          try { await fetch(`${apiUrl}/health`, { mode: 'no-cors' }); } catch (_) {}
          await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
          return fireRetry(attempt + 1);
        }
      } catch (err) {
        if (attempt < 3) {
          try { await fetch(`${apiUrl}/health`, { mode: 'no-cors' }); } catch (_) {}
          await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
          return fireRetry(attempt + 1);
        }
        console.error('Retry generation error:', err);
      }
    };
    fireRetry();

    startPolling(180000);
  };

  // Handle trip parameter changes from TripParametersPanel — update DB, delete activities, regenerate
  const handleParametersChanged = async (updatedFields) => {
    setIsRegenerating(true);
    setFeedbackBanner('regenerating');
    previousActivitiesRef.current = [...activities];
    try {
      // 1. Update itinerary in Supabase (try with all fields, fallback without newer columns)
      const { trip_origin, travel_mode, must_do_activities, who_is_coming, ...safeFields } = updatedFields;
      const fullPayload = { ...updatedFields, updated_at: new Date().toISOString() };
      const safePayload = { ...safeFields, updated_at: new Date().toISOString() };

      let { error: updateError } = await supabase
        .from('itineraries')
        .update(fullPayload)
        .eq('id', id);
      if (updateError) {
        // Retry without trip_origin/travel_mode if columns don't exist
        const { error: retryError } = await supabase
          .from('itineraries')
          .update(safePayload)
          .eq('id', id);
        if (retryError) throw retryError;
      }

      // 2. Update local state
      setItinerary(prev => ({ ...prev, ...updatedFields }));

      // 3. Delete existing activities so generation can replace them
      await supabase.from('activities').delete().eq('itinerary_id', id);
      setActivities([]);

      // 4. Fire background generation with retry on cold-start failure
      const apiUrl = process.env.REACT_APP_API_URL || 'https://travelatlas.onrender.com';
      const genBody = JSON.stringify({
        itineraryId: id,
        destination: updatedFields.destination || itinerary.destination,
        tripLength: updatedFields.trip_length || itinerary.trip_length,
        travelPace: updatedFields.travel_pace || itinerary.travel_pace,
        budget: updatedFields.budget || itinerary.budget,
        travelerProfiles: updatedFields.traveler_profiles || itinerary.traveler_profiles || [],
        region: null,
        tripOrigin: updatedFields.trip_origin || itinerary.trip_origin || null,
        travelMode: updatedFields.travel_mode || itinerary.travel_mode || null,
        mustDoActivities: updatedFields.must_do_activities || itinerary.must_do_activities || null,
        whoIsComing: updatedFields.who_is_coming || itinerary.who_is_coming || null,
      });
      const fireGeneration = async (attempt = 0) => {
        try {
          const res = await fetch(`${apiUrl}/api/ai/generate-itinerary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: genBody,
          });
          if (!res.ok && attempt < 3) {
            try { await fetch(`${apiUrl}/health`, { mode: 'no-cors' }); } catch (_) {}
            await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
            return fireGeneration(attempt + 1);
          }
        } catch (err) {
          if (attempt < 3) {
            try { await fetch(`${apiUrl}/health`, { mode: 'no-cors' }); } catch (_) {}
            await new Promise(r => setTimeout(r, attempt === 0 ? 5000 : 3000 + attempt * 3000));
            return fireGeneration(attempt + 1);
          }
          console.error('Regeneration error:', err);
        }
      };
      fireGeneration();

      // 5. Start polling for new activities (polling callbacks handle isRegenerating + feedbackBanner)
      startPolling(180000, updatedFields.trip_length || itinerary?.trip_length);
    } catch (error) {
      console.error('Error updating parameters:', error);
      setIsRegenerating(false);
      setFeedbackBanner(null);
      alert('Failed to update trip settings');
    }
  };

  useEffect(() => {
    if (authLoading) return;
    setLoading(true);
    fetchItineraryData();
  }, [id, authLoading]);

  // Start polling when navigated with generating=true OR when itinerary loads with 0 activities
  // and was created recently (within 5 min) — covers page refresh / re-visit scenarios
  useEffect(() => {
    if (authLoading || loading) return; // Wait for auth + initial fetch
    if (generatingActivities) return; // Already polling (e.g. from handleParametersChanged)
    if (isManualTrip) return; // Blank template — the user fills it in by hand, nothing to wait for

    const shouldPoll =
      (isGenerating && activities.length === 0) ||
      (!isGenerating && activities.length === 0 && itinerary && isRecentlyCreated(itinerary.created_at));

    if (shouldPoll) {
      const cleanup = startPolling(180000, itinerary?.trip_length);
      return cleanup;
    }
  }, [id, isGenerating, activities.length, loading, generatingActivities]);

  const fetchItineraryData = async () => {
    try {
      const { data: itineraryData, error: itineraryError } = await supabase
        .from('itineraries')
        .select('*')
        .eq('id', id)
        .single();

      if (itineraryError) throw itineraryError;
      setItinerary(itineraryData);
      setIsPublished(itineraryData.is_published || false);

      const [activitiesResult, accommodationsResult, dayNotesResult] = await Promise.all([
        supabase
          .from('activities')
          .select('*')
          .eq('itinerary_id', id)
          .order('day_number', { ascending: true })
          .order('position', { ascending: true }),
        supabase
          .from('accommodations')
          .select('*')
          .eq('itinerary_id', id),
        supabase
          .from('day_notes')
          .select('*')
          .eq('itinerary_id', id)
      ]);

      if (activitiesResult.error) throw activitiesResult.error;
      setActivities(activitiesResult.data || []);
      setAccommodations(augmentAccommodations(accommodationsResult.data || [], itineraryData.start_date));

      // Build day notes map { dayNumber: notes }
      if (dayNotesResult.data) {
        const notesMap = {};
        dayNotesResult.data.forEach(n => { notesMap[n.day_number] = n.notes; });
        setDayNotes(notesMap);
      }

      // Load day titles from itinerary
      setDayTitles(itineraryData.day_titles || {});
    } catch (error) {
      console.error('Error fetching itinerary:', error);
      alert('Failed to load itinerary');
      navigate('/designer');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('itineraries')
        .update({ updated_at: new Date().toISOString(), is_published: true, moderation_status: 'pending' })
        .eq('id', id);
      if (error) throw error;
      setIsPublished(true);
      setToast({ type: 'success', message: 'Itinerary saved! It will be visible to the community once approved by an admin.' });
    } catch (error) {
      console.error('Error saving itinerary:', error);
      setToast({ type: 'error', message: 'Failed to save itinerary. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Handle adding accommodation with date range (check-in / check-out)
  const handleAddAccommodationRange = async (hotel, checkInDay, checkOutDay) => {
    const checkInDate = itinerary.start_date
      ? (() => { const d = new Date(itinerary.start_date); d.setDate(d.getDate() + (checkInDay - 1)); return d.toISOString().split('T')[0]; })()
      : null;
    const checkOutDate = itinerary.start_date
      ? (() => { const d = new Date(itinerary.start_date); d.setDate(d.getDate() + (checkOutDay - 1)); return d.toISOString().split('T')[0]; })()
      : null;

    const newAcc = {
      itinerary_id: id,
      name: hotel.name,
      type: hotel.type || 'hotel',
      location: hotel.location || itinerary.destination,
      price_per_night: hotel.price_per_night || null,
      check_in_date: checkInDate,
      check_out_date: checkOutDate,
      latitude: hotel.latitude || null,
      longitude: hotel.longitude || null,
    };

    try {
      // Try with day number columns first, fall back without
      let result = await supabase.from('accommodations').insert({ ...newAcc, check_in_day: checkInDay, check_out_day: checkOutDay }).select().single();
      if (result.error) {
        result = await supabase.from('accommodations').insert(newAcc).select().single();
        if (result.error) throw result.error;
      }
      const data = result.data;
      // Always augment with day numbers locally for matching
      setAccommodations(prev => [...prev, { ...data, check_in_day: checkInDay, check_out_day: checkOutDay }]);
    } catch (error) {
      console.error('Error adding accommodation:', error);
      alert('Failed to add accommodation');
    }
  };

  const handleDeleteAccommodation = async (accId) => {
    try {
      const { error } = await supabase.from('accommodations').delete().eq('id', accId);
      if (error) throw error;
      setAccommodations(prev => prev.filter(a => a.id !== accId));
    } catch (error) {
      console.error('Error deleting accommodation:', error);
    }
  };

  // AI-suggest hotels for the whole trip. Booked accommodations (assigned to a
  // day) are kept in local state — only the unassigned suggestion pool is replaced.
  const handleGenerateHotels = async () => {
    if (!itinerary?.id || generatingHotels) return;
    setGeneratingHotels(true);
    try {
      const data = await generateHotels(itinerary.id);
      setAccommodations(prev => {
        const booked = prev.filter(a => a.check_in_date || a.check_in_day);
        return [...booked, ...(data.accommodations || [])];
      });
    } catch (error) {
      console.error('Hotel generation failed:', error);
    } finally {
      setGeneratingHotels(false);
    }
  };

  // Handle AI structural action (add/delete/replace day) — refetch data from DB
  const handleAIAction = async () => {
    try {
      // Refetch itinerary (trip_length may have changed) + activities
      const [itineraryResult, activitiesResult, accommodationsResult] = await Promise.all([
        supabase.from('itineraries').select('*').eq('id', id).single(),
        supabase.from('activities').select('*').eq('itinerary_id', id)
          .order('day_number', { ascending: true })
          .order('position', { ascending: true }),
        supabase.from('accommodations').select('*').eq('itinerary_id', id)
      ]);

      if (itineraryResult.data) setItinerary(itineraryResult.data);
      if (activitiesResult.data) setActivities(activitiesResult.data);
      if (accommodationsResult.data) setAccommodations(augmentAccommodations(accommodationsResult.data, itineraryResult.data?.start_date));

      // On mobile: switch to Days view so user sees the updated itinerary
      setMobilePanelView('days');
    } catch (error) {
      console.error('Error refetching after AI action:', error);
    }
  };

  // Handle adding activity from AI chat drag
  const handleAddActivityFromDrag = async (activityData, dayNumber) => {
    const validCategories = ['food', 'nature', 'culture', 'adventure', 'relaxation', 'shopping', 'nightlife', 'transport', 'accommodation', 'other'];
    const validTimeOfDay = ['morning', 'afternoon', 'evening', 'night', 'all-day'];

    // Validate and sanitize data to match DB constraints
    const duration = parseInt(activityData.duration_minutes) || 60;
    const costMin = activityData.estimated_cost_min ? parseFloat(activityData.estimated_cost_min) : null;
    const costMax = activityData.estimated_cost_max ? parseFloat(activityData.estimated_cost_max) : null;

    const newActivity = {
      itinerary_id: id,
      day_number: dayNumber,
      position: activities.filter(a => a.day_number === dayNumber).length,
      title: activityData.title || 'Untitled Activity',
      description: activityData.description || '',
      location: activityData.location || '',
      category: validCategories.includes(activityData.category) ? activityData.category : 'other',
      duration_minutes: duration > 0 ? duration : 60,
      estimated_cost_min: costMin !== null && costMin >= 0 ? costMin : null,
      estimated_cost_max: costMax !== null && costMin !== null && costMax >= costMin ? costMax : costMin,
      latitude: activityData.latitude && parseFloat(activityData.latitude) !== 0 ? parseFloat(activityData.latitude) : null,
      longitude: activityData.longitude && parseFloat(activityData.longitude) !== 0 ? parseFloat(activityData.longitude) : null,
      time_of_day: validTimeOfDay.includes(activityData.time_of_day) ? activityData.time_of_day : null
    };

    try {
      const { data, error } = await supabase
        .from('activities')
        .insert(newActivity)
        .select()
        .single();

      if (error) throw error;
      setActivities([...activities, data]);
    } catch (error) {
      console.error('Error adding activity:', error);
      console.error('Activity data:', newActivity);
      alert('Failed to add activity: ' + (error.message || 'Unknown error'));
    }
  };

  // Auto-publish when sharing. Runs the moderation gate server-side —
  // returns false (and alerts the reason) if the backend blocked it.
  const ensurePublished = async () => {
    if (isPublished) return true;
    try {
      await publishItinerary(id);
      setIsPublished(true);
      return true;
    } catch (error) {
      alert(error.message || 'Could not publish this itinerary.');
      return false;
    }
  };

  const handleCopyLink = async () => {
    if (!(await ensurePublished())) return;
    const shareUrl = `${window.location.origin}/itinerary/${id}`;
    await navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleShareWhatsApp = async () => {
    if (!(await ensurePublished())) return;
    const shareUrl = `${window.location.origin}/itinerary/${id}`;
    const text = `${itinerary.title} — ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShareOpen(false);
  };

  const handleNativeShare = async () => {
    if (!(await ensurePublished())) return;
    if (navigator.share) {
      const shareUrl = `${window.location.origin}/itinerary/${id}`;
      await navigator.share({ title: itinerary.title, url: shareUrl });
      setShareOpen(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setShareOpen(false);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const container = document.createElement('div');
      container.innerHTML = buildPrintableHTML(itinerary, activities, accommodations);
      document.body.appendChild(container);

      await html2pdf().set({
        margin: [10, 10, 10, 10],
        filename: `${itinerary.title || 'itinerary'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      }).from(container).save();

      document.body.removeChild(container);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const activeActivity = activities.find(a => a.id === active.id);
    const overActivity = activities.find(a => a.id === over.id);
    if (!activeActivity || !overActivity) return;

    const sourceDay = activeActivity.day_number;
    const targetDay = overActivity.day_number;

    if (sourceDay === targetDay) {
      // Same day: reorder within the day
      const dayActs = activities
        .filter(a => a.day_number === sourceDay)
        .sort((a, b) => a.position - b.position);
      const oldIndex = dayActs.findIndex(a => a.id === active.id);
      const newIndex = dayActs.findIndex(a => a.id === over.id);
      if (oldIndex === newIndex) return;

      const moved = arrayMove(dayActs, oldIndex, newIndex);
      const droppedTime = resolveDroppedTimeOfDay(moved, newIndex);
      const reordered = moved.map((a, i) => (
        i === newIndex ? { ...a, position: i, time_of_day: droppedTime } : { ...a, position: i }
      ));
      // Replace this day's activities in state with the reordered array (preserves visual order)
      const updatedActivities = [
        ...activities.filter(a => a.day_number !== sourceDay),
        ...reordered,
      ];
      setActivities(updatedActivities);

      try {
        await Promise.all(reordered.map((a, i) =>
          supabase
            .from('activities')
            .update(i === newIndex ? { position: i, time_of_day: droppedTime } : { position: i })
            .eq('id', a.id)
        ));
      } catch (error) {
        console.error('Error reordering activities:', error);
      }
    } else {
      // Cross-day: move activity from source day to target day
      const targetDayActs = activities
        .filter(a => a.day_number === targetDay && a.id !== active.id)
        .sort((a, b) => a.position - b.position);
      targetDayActs.push(activeActivity);
      const movedIndex = targetDayActs.length - 1;
      const droppedTime = resolveDroppedTimeOfDay(targetDayActs, movedIndex);
      const sortedTarget = targetDayActs.map((a, i) => ({
        ...a,
        day_number: targetDay,
        position: i,
        ...(i === movedIndex ? { time_of_day: droppedTime } : {}),
      }));

      const sourceDayActs = activities
        .filter(a => a.day_number === sourceDay && a.id !== active.id)
        .sort((a, b) => a.position - b.position)
        .map((a, i) => ({ ...a, position: i }));

      const updatedActivities = [
        ...activities.filter(a => a.day_number !== sourceDay && a.day_number !== targetDay),
        ...sortedTarget,
        ...sourceDayActs,
      ];
      setActivities(updatedActivities);

      try {
        const dbUpdates = [
          ...sortedTarget.map(a =>
            supabase.from('activities').update({ day_number: a.day_number, position: a.position }).eq('id', a.id)
          ),
          ...sourceDayActs.map(a =>
            supabase.from('activities').update({ position: a.position }).eq('id', a.id)
          ),
        ];
        await Promise.all(dbUpdates);
      } catch (error) {
        console.error('Error moving activity between days:', error);
      }
    }
  };

  const handleAddActivity = (dayNumber) => {
    setAddActivityDay(dayNumber);
  };

  const handleSaveNewActivity = async (activityData) => {
    const dayNumber = addActivityDay;
    const validCategories = ['food', 'nature', 'culture', 'adventure', 'relaxation', 'shopping', 'nightlife', 'transport', 'accommodation', 'other'];

    const dayActivities = activities.filter(a => a.day_number === dayNumber);

    const newActivity = {
      itinerary_id: id,
      day_number: dayNumber,
      position: 0,
      title: activityData.title,
      description: activityData.description || '',
      location: activityData.location || '',
      category: validCategories.includes(activityData.category) ? activityData.category : 'other',
      duration_minutes: activityData.duration_minutes || 60,
      time_of_day: activityData.time_of_day || null,
      estimated_cost_min: activityData.estimated_cost_min || null,
      estimated_cost_max: activityData.estimated_cost_max || null
    };

    try {
      const { data, error } = await supabase
        .from('activities')
        .insert(newActivity)
        .select()
        .single();

      if (error) throw error;

      // Sort all activities in the day by time of day
      const allDayActs = sortByTimeOfDay([...dayActivities, data]);
      const positionUpdates = allDayActs.map((a, i) =>
        supabase.from('activities').update({ position: i }).eq('id', a.id)
      );
      await Promise.all(positionUpdates);

      // Update local state with sorted positions
      const otherActivities = activities.filter(a => a.day_number !== dayNumber);
      const sorted = allDayActs.map((a, i) => ({ ...a, position: i }));
      setActivities([...otherActivities, ...sorted]);
    } catch (error) {
      console.error('Error adding activity:', error);
      alert('Failed to add activity');
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;

    try {
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', activityId);

      if (error) throw error;
      setActivities(activities.filter(a => a.id !== activityId));
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Failed to delete activity');
    }
  };

  const handleEditActivity = (activity) => {
    setEditActivity(activity);
  };

  const handleSaveNotes = async (activityId, notes) => {
    try {
      const { error } = await supabase
        .from('activities')
        .update({ custom_notes: notes })
        .eq('id', activityId);

      if (error) throw error;

      setActivities(activities.map(a =>
        a.id === activityId ? { ...a, custom_notes: notes } : a
      ));
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    }
  };

  const handleSaveEditedActivity = async (activityId, fields) => {
    try {
      const { error } = await supabase
        .from('activities')
        .update(fields)
        .eq('id', activityId);

      if (error) throw error;

      setActivities(activities.map(a =>
        a.id === activityId ? { ...a, ...fields } : a
      ));
    } catch (error) {
      console.error('Error updating activity:', error);
      alert('Failed to update activity');
    }
  };

  // Inline update of any activity field (used by TimelineView)
  const handleUpdateActivityField = async (activityId, fields) => {
    try {
      const { error } = await supabase
        .from('activities')
        .update(fields)
        .eq('id', activityId);
      if (error) throw error;
      setActivities(prev => prev.map(a =>
        a.id === activityId ? { ...a, ...fields } : a
      ));
    } catch (error) {
      console.error('Error updating activity:', error);
    }
  };

  // Save day-level notes (auto-save on blur)
  const handleSaveDayNote = async (dayNumber, text) => {
    setDayNotes(prev => ({ ...prev, [dayNumber]: text }));
    try {
      await supabase
        .from('day_notes')
        .upsert({ itinerary_id: id, day_number: dayNumber, notes: text, updated_at: new Date().toISOString() }, { onConflict: 'itinerary_id,day_number' });
    } catch (error) {
      console.error('Error saving day note:', error);
    }
  };

  // Swap two days: swap all activities, notes, titles, accommodations
  const handleSwapDays = async (dayA, dayB) => {
    // Swap activities day_number
    const updatedActivities = activities.map(a => {
      if (a.day_number === dayA) return { ...a, day_number: dayB };
      if (a.day_number === dayB) return { ...a, day_number: dayA };
      return a;
    });
    setActivities(updatedActivities);

    // Swap day titles
    const swappedTitles = { ...dayTitles };
    const titleA = swappedTitles[dayA];
    const titleB = swappedTitles[dayB];
    if (titleA) swappedTitles[dayB] = titleA; else delete swappedTitles[dayB];
    if (titleB) swappedTitles[dayA] = titleB; else delete swappedTitles[dayA];
    setDayTitles(swappedTitles);

    // Swap day notes
    const swappedNotes = { ...dayNotes };
    const noteA = swappedNotes[dayA];
    const noteB = swappedNotes[dayB];
    if (noteA !== undefined) swappedNotes[dayB] = noteA; else delete swappedNotes[dayB];
    if (noteB !== undefined) swappedNotes[dayA] = noteB; else delete swappedNotes[dayA];
    setDayNotes(swappedNotes);

    // Persist to DB
    try {
      // Update activities
      const activityUpdates = activities
        .filter(a => a.day_number === dayA || a.day_number === dayB)
        .map(a => {
          const newDay = a.day_number === dayA ? dayB : dayA;
          return supabase.from('activities').update({ day_number: newDay }).eq('id', a.id);
        });
      await Promise.all(activityUpdates);

      // Update day notes
      if (noteA !== undefined || noteB !== undefined) {
        const noteUpdates = [];
        // Delete both, then re-insert swapped
        await supabase.from('day_notes').delete().eq('itinerary_id', id).in('day_number', [dayA, dayB]);
        if (noteA !== undefined) {
          noteUpdates.push(supabase.from('day_notes').upsert(
            { itinerary_id: id, day_number: dayB, notes: noteA, updated_at: new Date().toISOString() },
            { onConflict: 'itinerary_id,day_number' }
          ));
        }
        if (noteB !== undefined) {
          noteUpdates.push(supabase.from('day_notes').upsert(
            { itinerary_id: id, day_number: dayA, notes: noteB, updated_at: new Date().toISOString() },
            { onConflict: 'itinerary_id,day_number' }
          ));
        }
        await Promise.all(noteUpdates);
      }

      // Update day titles on itinerary
      await supabase.from('itineraries')
        .update({ day_titles: swappedTitles, updated_at: new Date().toISOString() })
        .eq('id', id);
    } catch (error) {
      console.error('Error swapping days:', error);
    }
  };

  // Save day title (auto-save on blur)
  const handleDayTitleSave = async (dayNumber, title) => {
    const updated = { ...dayTitles, [dayNumber]: title };
    if (!title) delete updated[dayNumber];
    setDayTitles(updated);
    try {
      await supabase
        .from('itineraries')
        .update({ day_titles: updated, updated_at: new Date().toISOString() })
        .eq('id', id);
    } catch (error) {
      console.error('Error saving day title:', error);
    }
  };

  // Delete a day: remove its activities, shift subsequent days down, update trip_length
  const handleDeleteDay = async (dayNumber) => {
    if (!window.confirm(`Delete Day ${dayNumber} and all its activities?`)) return;
    try {
      // Delete activities for this day
      await supabase.from('activities').delete().eq('itinerary_id', id).eq('day_number', dayNumber);

      // Delete day notes for this day
      await supabase.from('day_notes').delete().eq('itinerary_id', id).eq('day_number', dayNumber);

      // Shift subsequent activities: day_number - 1 for all days after deleted day
      const laterActivities = activities.filter(a => a.day_number > dayNumber);
      for (const a of laterActivities) {
        await supabase.from('activities').update({ day_number: a.day_number - 1 }).eq('id', a.id);
      }

      // Shift subsequent day notes
      const laterNotesDays = Object.keys(dayNotes).map(Number).filter(d => d > dayNumber).sort((a, b) => a - b);
      for (const d of laterNotesDays) {
        await supabase.from('day_notes').upsert(
          { itinerary_id: id, day_number: d - 1, notes: dayNotes[d], updated_at: new Date().toISOString() },
          { onConflict: 'itinerary_id,day_number' }
        );
        // Delete the old day number entry
        await supabase.from('day_notes').delete().eq('itinerary_id', id).eq('day_number', d);
      }

      // Shift day titles
      const shiftedTitles = {};
      Object.entries(dayTitles).forEach(([d, title]) => {
        const num = Number(d);
        if (num < dayNumber) shiftedTitles[num] = title;
        else if (num > dayNumber) shiftedTitles[num - 1] = title;
      });

      // Update trip_length + day_titles
      const newLength = Math.max(1, (itinerary.trip_length || days.length) - 1);
      await supabase.from('itineraries').update({ trip_length: newLength, day_titles: shiftedTitles, updated_at: new Date().toISOString() }).eq('id', id);

      // Update local state
      setActivities(prev =>
        prev
          .filter(a => a.day_number !== dayNumber)
          .map(a => a.day_number > dayNumber ? { ...a, day_number: a.day_number - 1 } : a)
      );
      setDayNotes(prev => {
        const updated = {};
        Object.entries(prev).forEach(([d, text]) => {
          const num = Number(d);
          if (num < dayNumber) updated[num] = text;
          else if (num > dayNumber) updated[num - 1] = text;
        });
        return updated;
      });
      setDayTitles(shiftedTitles);
      setItinerary(prev => ({ ...prev, trip_length: newLength }));
    } catch (error) {
      console.error('Error deleting day:', error);
      alert('Failed to delete day');
    }
  };

  // Handle drop from AI chat
  const handleDrop = (e, dayNumber) => {
    e.preventDefault();
    setDragOverDay(null);
    const rawData = e.dataTransfer.getData('application/json');
    if (rawData) {
      const data = JSON.parse(rawData);
      handleAddActivityFromDrag(data, dayNumber);
    }
  };

  const handleDragOver = (e, dayNumber) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (dayNumber !== dragOverDay) {
      setDragOverDay(dayNumber);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the day block entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDay(null);
    }
  };

  // Open in Google Maps
  const openInGoogleMaps = (activity) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${activity.latitude},${activity.longitude}`;
    window.open(url, '_blank');
  };

  const openAllInGoogleMaps = () => {
    const activitiesWithCoords = activities.filter(a => a.latitude && a.longitude);
    const filtered = selectedDay === 'all'
      ? activitiesWithCoords
      : activitiesWithCoords.filter(a => a.day_number === parseInt(selectedDay));

    if (filtered.length === 0) return;
    const waypoints = filtered.map(a => `${a.latitude},${a.longitude}`).join('/');
    const url = `https://www.google.com/maps/dir/${waypoints}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-naples-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400 mx-auto mb-4"></div>
          <p className="text-platinum-600">Loading your itinerary...</p>
        </div>
      </div>
    );
  }

  if (!itinerary) return null;

  // No full-screen generating page — show main planner with skeletons instead

  // Timed-out generating page — shown when polling expired without activities
  if (generationTimedOut && activities.length === 0) {
    return (
      <div className="min-h-screen bg-naples-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
            <span className="text-3xl">&#x23F3;</span>
          </div>

          <h1 className="text-3xl font-semibold text-charcoal-500 mb-3">
            Taking Longer Than Usual
          </h1>
          <p className="text-charcoal-400 mb-6 max-w-sm mx-auto">
            The server is still working on your {itinerary.destination} itinerary. This sometimes happens on the first request.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleRetryGeneration}
              className="px-8 py-3 bg-coral-400 text-white rounded-xl font-bold hover:bg-coral-500 transition-colors"
            >
              Retry Generation
            </button>
            <button
              onClick={() => {
                const cleanup = startPolling(120000);
                return cleanup;
              }}
              className="px-8 py-3 bg-white border border-platinum-200 text-charcoal-500 rounded-xl font-medium hover:bg-platinum-50 transition-colors"
            >
              Keep Waiting
            </button>
            <button
              onClick={() => { setGenerationTimedOut(false); setGeneratingActivities(false); }}
              className="px-8 py-3 bg-white border border-platinum-200 text-charcoal-500 rounded-xl font-medium hover:bg-platinum-50 transition-colors"
            >
              Start Blank — Build My Own
            </button>
            <button
              onClick={() => navigate('/designer')}
              className="text-sm text-platinum-500 hover:text-charcoal-500 underline mt-2"
            >
              Go back to dashboard
            </button>
          </div>

          <p className="text-xs text-platinum-500 mt-6">
            Tip: If you come back to this trip later, your activities will be here once the server finishes.
          </p>
        </div>
      </div>
    );
  }

  // Render notes modal if active
  const renderNotesModal = notesActivity && (
    <NotesModal
      activity={notesActivity}
      onClose={() => setNotesActivity(null)}
      onSave={handleSaveNotes}
    />
  );

  // Render add activity modal if active
  const addActivityDayLabel = addActivityDay
    ? (itinerary.start_date
        ? getDateForDay(itinerary.start_date, addActivityDay)
        : `Day ${addActivityDay}`)
    : '';

  const renderAddActivityModal = addActivityDay && (
    <AddActivityModal
      dayNumber={addActivityDay}
      dayLabel={addActivityDayLabel}
      onClose={() => setAddActivityDay(null)}
      onSave={handleSaveNewActivity}
    />
  );

  // Render edit activity modal if active
  const renderEditActivityModal = editActivity && (
    <EditActivityModal
      activity={editActivity}
      onClose={() => setEditActivity(null)}
      onSave={handleSaveEditedActivity}
    />
  );

  // Find accommodation for a specific day number
  const getAccommodationForDay = (dayNumber) => {
    return accommodations.find(acc => {
      // Use day numbers if available
      if (acc.check_in_day && acc.check_out_day) {
        return dayNumber >= acc.check_in_day && dayNumber < acc.check_out_day;
      }
      // Fall back to dates
      if (!itinerary.start_date || !acc.check_in_date || !acc.check_out_date) return false;
      const dayDate = new Date(itinerary.start_date);
      dayDate.setDate(dayDate.getDate() + (dayNumber - 1));
      const dayStr = dayDate.toISOString().split('T')[0];
      return dayStr >= acc.check_in_date && dayStr < acc.check_out_date;
    });
  };

  // Accommodations already assigned to some day — the rest are unassigned AI suggestions
  const assignedAccommodationIds = new Set(
    accommodations.filter(a => a.check_in_date || a.check_in_day).map(a => a.id)
  );

  // How many consecutive days (from `day` onward) stay in the same city —
  // used to recommend a night count instead of defaulting to 1.
  const getRecommendedNightsForDay = (day) => {
    const baseCities = new Set(
      activities.filter(a => a.day_number === day).map(a => a.city_name).filter(Boolean)
    );
    if (baseCities.size === 0) return 1;
    let nights = 1;
    for (let d = day + 1; d <= itinerary.trip_length; d++) {
      const nextCities = activities.filter(a => a.day_number === d).map(a => a.city_name).filter(Boolean);
      if (!nextCities.some(c => baseCities.has(c))) break;
      nights++;
    }
    return nights;
  };

  // Unassigned hotel suggestions whose location matches this day's city
  const getSuggestedHotelsForDay = (day) => {
    const suggestions = accommodations.filter(a => !assignedAccommodationIds.has(a.id));
    if (suggestions.length === 0) return [];

    const dayCities = [...new Set(
      activities.filter(a => a.day_number === day).map(a => a.city_name).filter(Boolean)
    )];
    const cities = dayCities.length > 0
      ? dayCities
      : [...new Set(activities.map(a => a.city_name).filter(Boolean))].length === 1
        ? [...new Set(activities.map(a => a.city_name).filter(Boolean))]
        : [];
    if (cities.length === 0) return [];

    const recommendedNights = getRecommendedNightsForDay(day);
    return suggestions
      .filter(h => cities.some(c => h.location?.toLowerCase().includes(c.toLowerCase())))
      .slice(0, 2)
      .map(h => ({ ...h, _recommendedNights: recommendedNights }));
  };

  // Select a suggested hotel for a day — assigns it immediately for the
  // recommended number of nights (no extra confirmation step).
  const handleSelectHotel = (hotel, day) => {
    const nights = Math.max(1, hotel._recommendedNights || getRecommendedNightsForDay(day));
    const checkOutDay = Math.min(day + nights, itinerary.trip_length + 1);
    handleAddAccommodationRange(hotel, day, checkOutDay);
  };

  // Grow/shrink an already-assigned accommodation's stay by one night.
  // Adjacent days automatically pick up (or lose) coverage via getAccommodationForDay.
  const handleAdjustAccommodationNights = async (acc, delta) => {
    const checkInDay = acc.check_in_day;
    if (!checkInDay) return;
    const currentNights = acc.check_out_day - checkInDay;
    const newNights = Math.max(1, currentNights + delta);
    const newCheckOutDay = Math.min(checkInDay + newNights, itinerary.trip_length + 1);
    if (newCheckOutDay === acc.check_out_day) return;

    const checkOutDate = itinerary.start_date
      ? (() => { const d = new Date(itinerary.start_date); d.setDate(d.getDate() + (newCheckOutDay - 1)); return d.toISOString().split('T')[0]; })()
      : null;

    try {
      const { error } = await supabase.from('accommodations').update({ check_out_date: checkOutDate }).eq('id', acc.id);
      if (error) throw error;
      setAccommodations(prev => prev.map(a =>
        a.id === acc.id ? { ...a, check_out_date: checkOutDate, check_out_day: newCheckOutDay } : a
      ));
    } catch (error) {
      console.error('Error adjusting accommodation nights:', error);
    }
  };

  const days = Array.from({ length: itinerary.trip_length }, (_, i) => i + 1);
  const dayLocationLabels = getDayLocationLabels(activities, days);

  return (
    <div className="h-screen flex flex-col bg-naples-100">
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-[60]">
        <AnimatePresence>
          {toast && (
            <Toast type={toast.type} message={toast.message} onClose={dismissToast} />
          )}
        </AnimatePresence>
      </div>
      {renderNotesModal}
      {renderAddActivityModal}
      {renderEditActivityModal}
      {showFullScreenMap && (
        <FullScreenMapModal
          activities={activities}
          days={days}
          itinerary={itinerary}
          onClose={() => setShowFullScreenMap(false)}
        />
      )}
      {/* Top Navigation */}
      <div className="bg-charcoal-500 px-4 md:px-6 py-3.5 flex-shrink-0">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <button
              onClick={() => navigate('/designer')}
              className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-sm flex-shrink-0"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="w-px h-6 bg-white/20 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base font-bold text-white leading-tight truncate max-w-[150px] sm:max-w-xs md:max-w-none">
                {itinerary.title}
              </h1>
              <p className="text-xs text-white/55 mt-0.5 hidden sm:block">
                {itinerary.destination} · {itinerary.trip_length} days
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAssistant(!showAssistant)}
              title={showAssistant ? 'Hide AI assistant' : 'Show AI assistant'}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-all"
            >
              {showAssistant ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
              AI Panel
            </button>
            <button
              onClick={() => setShowMap(!showMap)}
              title={showMap ? 'Hide map' : 'Show map'}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-all"
            >
              <PanelLeftClose size={14} className={showMap ? '' : 'rotate-180'} />
              Map
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-coral-400 hover:bg-coral-500 rounded-lg transition-colors disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <SendToConcierge
              itineraryId={id}
              tripTitle={itinerary.title}
              destination={itinerary.destination}
              tripLength={itinerary.trip_length}
              source="planner"
              label="Atlas Plan"
              compact
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-all"
            />
            <div className="relative">
              <button
                onClick={() => setShareOpen(!shareOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-all"
              >
                <Share2 size={14} />
                Share
              </button>
              {shareOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShareOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl shadow-lg border border-platinum-200 py-1 w-52">
                    <button
                      onClick={handleShareWhatsApp}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
                    >
                      <MessageCircle size={16} />
                      WhatsApp
                    </button>
                    {typeof navigator.share === 'function' && (
                      <button
                        onClick={handleNativeShare}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
                      >
                        <Share2 size={16} />
                        Messages
                      </button>
                    )}
                    <button
                      onClick={() => { handleCopyLink(); setShareOpen(false); }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full"
                    >
                      {linkCopied ? <Check size={16} className="text-green-500" /> : <Link2 size={16} />}
                      {linkCopied ? 'Copied!' : 'Copy Link'}
                    </button>
                    <div className="border-t border-platinum-200 my-1" />
                    <button
                      onClick={handleExport}
                      disabled={exporting}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors w-full disabled:opacity-50"
                    >
                      <Download size={16} />
                      {exporting ? 'Exporting...' : 'Export as PDF'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop layout — 3-panel resizable, hidden on mobile */}
      <div className="hidden md:block flex-1 overflow-hidden">
        <div className="w-full max-w-[1800px] mx-auto h-full p-4">
          <PanelGroup orientation="horizontal" id="planner-layout">

          {/* AI Panel — collapsible, slides in from left */}
          {showAssistant && (
            <>
              <Panel defaultSize="25%" minSize="18%" maxSize="38%" id="ai-panel">
                <div className="h-full flex flex-col min-w-0">
                  <LeftPanel
                    itinerary={itinerary}
                    activities={activities}
                    accommodations={accommodations}
                    onActionExecuted={handleAIAction}
                    onAddAccommodation={handleAddAccommodationRange}
                    onAddActivityFromChat={handleAddActivityFromDrag}
                    onParametersChanged={handleParametersChanged}
                    onInsightsUpdated={(newInsights) =>
                      setItinerary(prev => prev ? { ...prev, ai_insights: newInsights } : prev)
                    }
                    isRegenerating={isRegenerating}
                    onStartBlank={() => { setGeneratingActivities(false); setGenerationTimedOut(false); }}
                  />
                </div>
              </Panel>
              <PanelResizeHandle className="w-2 flex items-center justify-center hover:bg-platinum-100 rounded transition-colors">
                <div className="w-0.5 h-8 bg-platinum-300 hover:bg-charcoal-300 rounded-full transition-colors" />
              </PanelResizeHandle>
            </>
          )}

          {/* Day List — always visible */}
          <Panel defaultSize={!showMap ? "100%" : showAssistant ? "38%" : "42%"} minSize="22%" id="overview-panel">
            <div className="h-full flex flex-col bg-white rounded-2xl shadow-md overflow-hidden">

              {/* Feedback banners */}
              {feedbackBanner === 'regenerating' && (
                <div className="mx-4 mt-3 px-4 py-3 bg-naples-100 border border-naples-300 rounded-lg flex items-center gap-3 text-sm text-naples-800">
                  <div className="w-4 h-4 border-2 border-naples-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  <span>{progressMessage || 'Updating itinerary...'}</span>
                </div>
              )}
              {!feedbackBanner && generatingActivities && activities.length === 0 && (
                <div className="mx-4 mt-3 px-4 py-3 bg-coral-50 border border-coral-200 rounded-lg flex items-center gap-3 text-sm text-coral-700">
                  <div className="w-4 h-4 border-2 border-coral-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  <span>{progressMessage || 'Generating your itinerary...'}</span>
                </div>
              )}
              {feedbackBanner === 'done' && (
                <div className="mx-4 mt-3 px-4 py-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-700">
                  <Check size={14} />
                  Itinerary updated! Changed days are highlighted.
                </div>
              )}

              {/* Atlas Plan CTA */}
              {activities.length > 0 && !generatingActivities && !atlasPlanDismissed && (
                <div className="mx-4 mt-2">
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-gradient-to-r from-coral-50 to-naples-50 border border-coral-200 rounded-xl">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Sparkles size={14} className="text-coral-400 flex-shrink-0" />
                      <p className="text-xs text-charcoal-500 leading-snug">
                        <span className="font-semibold">Rather have an expert build it?</span>
                        <span className="text-charcoal-400 ml-1">Atlas Plan crafts personalised itineraries — free quote.</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a href="/atlas-plan" className="text-xs font-semibold px-3 py-1.5 bg-coral-400 text-white rounded-lg hover:bg-coral-500 transition-colors whitespace-nowrap">
                        Get a quote →
                      </a>
                      <button onClick={() => setAtlasPlanDismissed(true)} className="text-platinum-400 hover:text-platinum-600 transition-colors">
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Day list */}
              <div className="flex-1 overflow-y-auto p-4">
                <OverviewView
                  days={days}
                  activities={activities}
                  itinerary={itinerary}
                  sensors={sensors}
                  activeId={activeId}
                  dragOverDay={dragOverDay}
                  dayLocationLabels={dayLocationLabels}
                  dayTitles={dayTitles}
                  generatingActivities={generatingActivities}
                  highlightedDays={highlightedDays}
                  getAccommodationForDay={getAccommodationForDay}
                  getSuggestedHotelsForDay={getSuggestedHotelsForDay}
                  onGenerateHotels={handleGenerateHotels}
                  generatingHotels={generatingHotels}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onAddActivity={handleAddActivity}
                  onEditActivity={handleEditActivity}
                  onDeleteActivity={handleDeleteActivity}
                  onSaveNotes={handleSaveNotes}
                  onDeleteAccommodation={handleDeleteAccommodation}
                  onSelectHotel={handleSelectHotel}
                  onAdjustAccommodationNights={handleAdjustAccommodationNights}
                  onDeleteDay={handleDeleteDay}
                  onDayTitleSave={handleDayTitleSave}
                  onSwapDays={handleSwapDays}
                  selectedActivityId={selectedActivityId}
                  onActivitySelect={setSelectedActivityId}
                  onOpenAI={() => setShowAssistant(true)}
                />
              </div>
            </div>
          </Panel>

          {showMap && (
            <>
              <PanelResizeHandle className="w-2 flex items-center justify-center hover:bg-platinum-100 rounded transition-colors">
                <div className="w-0.5 h-8 bg-platinum-300 hover:bg-charcoal-300 rounded-full transition-colors" />
              </PanelResizeHandle>

              {/* Map — toggled via "Map" button in top bar */}
              <Panel defaultSize={showAssistant ? "37%" : "58%"} minSize="20%" id="map-panel">
                <div className="h-full rounded-2xl shadow-md overflow-hidden">
                  <MapTabView
                    activities={activities}
                    days={days}
                    itinerary={itinerary}
                    selectedActivityId={selectedActivityId}
                    onActivitySelect={setSelectedActivityId}
                  />
                </div>
              </Panel>
            </>
          )}

          </PanelGroup>
        </div>
      </div>

      {/* Mobile layout — single full-screen panel + bottom tab bar, hidden on desktop */}
      <div className="md:hidden flex-1 relative overflow-hidden">

        {/* Days panel — always mounted, shown by default */}
        <div className={`absolute inset-0 flex flex-col bg-white overflow-hidden pb-16 transition-opacity duration-150 ${mobilePanelView !== 'days' ? 'opacity-0 pointer-events-none' : ''}`}>
          {feedbackBanner === 'regenerating' && (
            <div className="mx-4 mt-3 px-4 py-3 bg-naples-100 border border-naples-300 rounded-lg flex items-center gap-3 text-sm text-naples-800">
              <div className="w-4 h-4 border-2 border-naples-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
              <span>{progressMessage || 'Updating itinerary...'}</span>
            </div>
          )}
          {!feedbackBanner && generatingActivities && activities.length === 0 && (
            <div className="mx-4 mt-3 px-4 py-3 bg-coral-50 border border-coral-200 rounded-lg flex items-center gap-3 text-sm text-coral-700">
              <div className="w-4 h-4 border-2 border-coral-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
              <span>{progressMessage || 'Generating your itinerary...'}</span>
            </div>
          )}
          {feedbackBanner === 'done' && (
            <div className="mx-4 mt-3 px-4 py-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-700">
              <Check size={14} />
              Itinerary updated! Changed days are highlighted.
            </div>
          )}
          {activities.length > 0 && !generatingActivities && !atlasPlanDismissed && (
            <div className="mx-4 mt-2">
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-gradient-to-r from-coral-50 to-naples-50 border border-coral-200 rounded-xl">
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkles size={13} className="text-coral-400 flex-shrink-0" />
                  <p className="text-xs text-charcoal-500 leading-snug truncate">
                    <span className="font-semibold">Want an expert to build this?</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a href="/atlas-plan" className="text-xs font-semibold px-2.5 py-1.5 bg-coral-400 text-white rounded-lg hover:bg-coral-500 transition-colors whitespace-nowrap">
                    Get a quote →
                  </a>
                  <button onClick={() => setAtlasPlanDismissed(true)} className="text-platinum-400 hover:text-platinum-600 transition-colors">
                    <X size={13} />
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-4">
            <OverviewView
              days={days}
              activities={activities}
              itinerary={itinerary}
              sensors={sensors}
              activeId={activeId}
              dragOverDay={dragOverDay}
              dayLocationLabels={dayLocationLabels}
              dayTitles={dayTitles}
              generatingActivities={generatingActivities}
              highlightedDays={highlightedDays}
              getAccommodationForDay={getAccommodationForDay}
              getSuggestedHotelsForDay={getSuggestedHotelsForDay}
              onGenerateHotels={handleGenerateHotels}
              generatingHotels={generatingHotels}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onAddActivity={handleAddActivity}
              onEditActivity={handleEditActivity}
              onDeleteActivity={handleDeleteActivity}
              onSaveNotes={handleSaveNotes}
              onDeleteAccommodation={handleDeleteAccommodation}
              onSelectHotel={handleSelectHotel}
              onAdjustAccommodationNights={handleAdjustAccommodationNights}
              onDeleteDay={handleDeleteDay}
              onDayTitleSave={handleDayTitleSave}
              onSwapDays={handleSwapDays}
              selectedActivityId={selectedActivityId}
              onActivitySelect={setSelectedActivityId}
              onOpenAI={() => { setShowAssistant(true); setMobilePanelView('ai'); setAiMounted(true); }}
            />
          </div>
        </div>

        {/* Map panel — lazy-mounted so Leaflet initialises with real container dimensions */}
        {mapMounted && (
          <div className={`absolute inset-0 pb-16 transition-opacity duration-150 ${mobilePanelView !== 'map' ? 'opacity-0 pointer-events-none' : ''}`}>
            <MapTabView
              activities={activities}
              days={days}
              itinerary={itinerary}
              selectedActivityId={selectedActivityId}
              onActivitySelect={setSelectedActivityId}
            />
          </div>
        )}

        {/* AI panel — lazy-mounted to preserve chat state once opened */}
        {aiMounted && (
          <div className={`absolute inset-0 pb-16 overflow-hidden transition-opacity duration-150 ${mobilePanelView !== 'ai' ? 'opacity-0 pointer-events-none' : ''}`}>
            <LeftPanel
              itinerary={itinerary}
              activities={activities}
              accommodations={accommodations}
              onActionExecuted={handleAIAction}
              onAddAccommodation={handleAddAccommodationRange}
              onAddActivityFromChat={handleAddActivityFromDrag}
              onParametersChanged={handleParametersChanged}
              onInsightsUpdated={(newInsights) =>
                setItinerary(prev => prev ? { ...prev, ai_insights: newInsights } : prev)
              }
              isRegenerating={isRegenerating}
              onStartBlank={() => { setGeneratingActivities(false); setGenerationTimedOut(false); }}
            />
          </div>
        )}

        {/* Bottom tab bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-platinum-200 z-30 flex">
          <button
            onClick={() => setMobilePanelView('days')}
            className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-semibold transition-colors ${mobilePanelView === 'days' ? 'text-coral-500' : 'text-platinum-400'}`}
          >
            <LayoutList size={20} />
            Days
          </button>
          <button
            onClick={() => { setMobilePanelView('map'); setMapMounted(true); }}
            className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-semibold transition-colors ${mobilePanelView === 'map' ? 'text-coral-500' : 'text-platinum-400'}`}
          >
            <MapIcon size={20} />
            Map
          </button>
          <button
            onClick={() => { setMobilePanelView('ai'); setAiMounted(true); }}
            className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-semibold transition-colors ${mobilePanelView === 'ai' ? 'text-coral-500' : 'text-platinum-400'}`}
          >
            <Sparkles size={20} />
            AI
          </button>
        </div>
      </div>
    </div>
  );
}

// Build printable HTML for PDF export
function buildPrintableHTML(itinerary, activities, accommodations) {
  const days = Array.from({ length: itinerary.trip_length }, (_, i) => i + 1);
  const pdfLocationLabels = getDayLocationLabels(activities, days);
  const DAY_COLORS_PDF = [
    '#EF8557', '#4A7B91', '#FFDB70', '#2C4251', '#BBD3DD',
    '#C85A2E', '#7DADC1', '#F0C84D', '#386074', '#DEE3E1'
  ];

  const fmtDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const dayDate = (startDate, dayNumber) => {
    if (!startDate) return `Day ${dayNumber}`;
    const d = new Date(startDate);
    d.setDate(d.getDate() + (dayNumber - 1));
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  let html = `
    <div style="font-family: 'Inter', 'Helvetica', sans-serif; color: #2C4251; max-width: 700px; margin: 0 auto;">
      <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #EF8557;">
        <h1 style="font-size: 28px; margin: 0 0 8px 0; color: #2C4251;">${itinerary.title || 'Trip Itinerary'}</h1>
        <p style="font-size: 14px; color: #71717A; margin: 0;">
          ${itinerary.destination} &bull; ${itinerary.trip_length} days
          ${itinerary.start_date ? ` &bull; ${fmtDate(itinerary.start_date)} — ${fmtDate(itinerary.end_date)}` : ''}
        </p>
      </div>
  `;

  days.forEach(day => {
    const dayActivities = activities.filter(a => a.day_number === day);
    const label = dayDate(itinerary.start_date, day);
    const locationLabel = pdfLocationLabels[day];

    html += `
      <div style="margin-bottom: 24px; page-break-inside: avoid;">
        <div style="background: #F5F3F0; padding: 10px 16px; border-radius: 8px; margin-bottom: 12px; border-left: 4px solid ${DAY_COLORS_PDF[(day - 1) % DAY_COLORS_PDF.length]};">
          <strong style="font-size: 16px;">Day ${day} — ${label}</strong>
          ${locationLabel ? `<span style="color: #71717A; font-size: 13px; margin-left: 8px;">${locationLabel}</span>` : ''}
        </div>
    `;

    if (dayActivities.length === 0) {
      html += `<p style="color: #71717A; font-size: 13px; font-style: italic; padding-left: 20px;">No activities planned</p>`;
    } else {
      dayActivities.forEach(activity => {
        html += `
          <div style="padding: 8px 0 8px 20px; border-bottom: 1px solid #f0f0f0;">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 2px;">${activity.title}</div>
            ${activity.description ? `<div style="font-size: 12px; color: #71717A; margin-bottom: 2px;">${activity.description}</div>` : ''}
            <div style="font-size: 11px; color: #9CA3AF;">
              ${activity.location ? `${activity.location}` : ''}
              ${activity.duration_minutes ? ` &bull; ${Math.floor(activity.duration_minutes / 60)}h${activity.duration_minutes % 60 > 0 ? ` ${activity.duration_minutes % 60}m` : ''}` : ''}
              ${activity.time_of_day ? ` &bull; ${activity.time_of_day}` : ''}
              ${activity.estimated_cost_min != null ? ` &bull; $${activity.estimated_cost_min}${activity.estimated_cost_max && activity.estimated_cost_max !== activity.estimated_cost_min ? `–$${activity.estimated_cost_max}` : ''}` : ''}
            </div>
          </div>
        `;
      });
    }

    html += `</div>`;
  });

  if (accommodations.length > 0) {
    html += `
      <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #4A7B91;">
        <h2 style="font-size: 18px; margin: 0 0 12px 0;">Accommodations</h2>
    `;
    accommodations.forEach(acc => {
      html += `
        <div style="padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <strong style="font-size: 14px;">${acc.name}</strong>
          <div style="font-size: 12px; color: #71717A;">
            ${acc.location || ''}
            ${acc.price_per_night ? ` &bull; $${acc.price_per_night}/night` : ''}
            ${acc.check_in_date ? ` &bull; ${fmtDate(acc.check_in_date)} — ${fmtDate(acc.check_out_date)}` : ''}
          </div>
        </div>
      `;
    });
    html += `</div>`;
  }

  html += `
      <div style="text-align: center; margin-top: 30px; padding-top: 16px; border-top: 1px solid #e0e0e0;">
        <p style="font-size: 11px; color: #9CA3AF;">Created with Travel Atlas</p>
      </div>
    </div>
  `;

  return html;
}

export default PlannerPage;
